/**
 * Utilities for working with Monaco Editor
 */

// Configuration for Monaco environment to handle worker loading errors
export const configureMonacoEnvironment = () => {
    window.MonacoEnvironment = {
        getWorkerUrl: function(_, label) {
            return `data:text/javascript;charset=utf-8,${encodeURIComponent(`
                self.MonacoEnvironment = {
                    baseUrl: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/'
                };
                try {
                    importScripts('https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs/base/worker/workerMain.js');
                } catch (e) {
                    console.warn('Monaco worker script failed to load:', e);
                    self.onmessage = function() {};
                }
            `)}`;
        }
    };
};

// Function to clean up Monaco resources
export const cleanupMonacoResources = (disposables: any[], editorRef: any, monacoRef: any) => {
    // Clean up registered providers and other resources
    if (disposables?.length) {
        disposables.forEach(disposable => {
            if (disposable?.dispose) {
                try { 
                    disposable.dispose(); 
                } catch (e) { 
                    console.warn('Error releasing resource:', e); 
                }
            }
        });
    }
    
    // Clean up references
    if (editorRef) {
        editorRef.current = null;
    }
    
    if (monacoRef) {
        monacoRef.current = null;
    }
    
    return [];
};

// Configure country suggestions for autocompletion
export const setupCountrySuggestions = (monaco: any, countries: any[]): any => {
    // Only register if we have countries data
    if (!countries || countries.length === 0) {
        return null;
    }
    
    return monaco.languages.registerCompletionItemProvider('pennicode', {
        triggerCharacters: ['.'],
        provideCompletionItems: (model: any, position: any) => {
            const lineContent = model.getLineContent(position.lineNumber);
            const textUntilPosition = lineContent.substring(0, position.column);
            
            // Only activate for the 'country.' pattern
            if (!textUntilPosition.endsWith('country.')) {
                return { suggestions: [] };
            }
            
            // Exit if no countries available
            if (!countries.length) {
                return { suggestions: [] };
            }
            
            // Define range for autocompletion
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: position.column,
                endColumn: position.column
            };
            
            // Return country suggestions
            return {
                suggestions: countries.map((country: any) => ({
                    label: country.country_name,
                    kind: monaco.languages.CompletionItemKind.Value,
                    insertText: country.country_id.toString(),
                    detail: `ID: ${country.country_id}, Currency: ${country.currency}`,
                    sortText: '0', // High priority
                    range: range
                }))
            };
        }
    });
};

// Configure pennicode language to inherit JavaScript features
export const configurePennicodeLanguage = (monaco: any): any => {
  // Only register the language once per Monaco instance
  try {
    monaco.languages.register({ id: 'pennicode' });
  } catch (e) {
    // Language already registered, which is fine
  }

  monaco.languages.setMonarchTokensProvider('pennicode', {
    defaultToken: '',
    tokenPostfix: '.js',
    keywords: [
      'break', 'case', 'catch', 'class', 'continue', 'const', 'default',
      'delete', 'do', 'else', 'export', 'extends', 'finally', 'for',
      'from', 'function', 'if', 'import', 'in', 'instanceof', 'let',
      'new', 'return', 'super', 'switch', 'this', 'throw', 'try',
      'typeof', 'var', 'void', 'while', 'with', 'yield'
    ],
    symbols: /[=><!~?:&|+\-*/^%]+/,
    escapes: /\\(?:[abfnrtv\\"'n]|u[0-9A-Fa-f]{4})/,
    tokenizer: {
      root: [
        // Comments
        [/\/\/.*$/, 'comment'],
        [/\/\*/, 'comment', '@comment'],

        // Strings
        [/("([^"\\]|\\.)*$)/, 'string.invalid'],
        [/'([^'\\]|\\.)*$/, 'string.invalid'],
        [/"([^"\\]|\\.)*"/, 'string'],
        [/'([^'\\]|\\.)*'/, 'string'],

        // Object property: .propertyName
        [/\.[a-zA-Z_$][\w$]*/, 'property'],

        // Keywords & identifiers
        [/[a-zA-Z_$][\w$]*/, {
          cases: {
            '@keywords': 'keyword',
            '@default': 'identifier'
          }
        }],

        // Numbers
        [/\d+(\.\d+)?/, 'number'],

        // Operators
        [/[=><!~?:&|+\-*/^%]+/, 'operator'],

        // Brackets
        [/[[\]{}()]/, '@brackets']
      ],
      comment: [
        [/[^\/*]+/, 'comment'],
        [/\*\//, 'comment', '@pop'],
        [/[/\*]/, 'comment']
      ]
    }
  });

  // Configure bracket matching and auto-closing
  return monaco.languages.setLanguageConfiguration('pennicode', {
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')']
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: '`', close: '`' }
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: '`', close: '`' }
    ]
  });
};

// Configura sugerencias personalizadas para el objeto cart
export const setupCartSuggestions = (monaco: any): any => {
  const provider1 = monaco.languages.registerCompletionItemProvider('pennicode', {
    provideCompletionItems: function(model: any, position: any) {
      const lineContent = model.getLineContent(position.lineNumber);
      const textUntilPosition = lineContent.substring(0, position.column);
      
      const suggestions = [
        {
          label: 'cart',
          kind: monaco.languages.CompletionItemKind.Variable,
          documentation: 'Shopping cart object',
          insertText: 'cart',
        },
        {
          label: 'shippingMethods',
          kind: monaco.languages.CompletionItemKind.Variable,
          documentation: 'Available shipping methods array',
          insertText: 'shippingMethods',
        },
        {
          label: 'shippingMethods[INDEX]',
          kind: monaco.languages.CompletionItemKind.Snippet,
          documentation: 'Access specific shipping method by index',
          insertText: 'shippingMethods[${1:0}]',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        }
      ];

      return { suggestions: suggestions };
    }
  });
  
  const provider2 = monaco.languages.registerCompletionItemProvider('pennicode', {
    triggerCharacters: ['.'],
    provideCompletionItems: (model: any, position: any) => {
      const lineContent = model.getLineContent(position.lineNumber);
      const textUntilPosition = lineContent.substring(0, position.column);

      // Removed early return that required textUntilPosition to end with 'cart.'
      if (!textUntilPosition.includes('cart.') && !textUntilPosition.includes('shippingMethods')) {
        return { suggestions: [] };
      }

      if (textUntilPosition.endsWith('shippingMethods.')) {
        return {
          suggestions: [
            { label: 'push', kind: monaco.languages.CompletionItemKind.Method, insertText: 'push({\n "shipping_id": SHIPPING_ID,\n "rate_name": "NAME",\n "price": PRICE,\n})', documentation: 'Add a new shipping method to the array' },
            { label: 'findIndex', kind: monaco.languages.CompletionItemKind.Method, insertText: 'findIndex(method => method.shipping_id === SHIPPING_ID)', documentation: 'Find the index of a shipping method' },
            { label: 'length', kind: monaco.languages.CompletionItemKind.Property, insertText: 'length', documentation: 'Number of shipping methods' },
          ]
        };
      } else if (textUntilPosition.includes('shippingMethods[') && textUntilPosition.includes('].') && !textUntilPosition.includes('cart.')) {
        return {
          suggestions: [
            { label: 'shipping_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_id', documentation: 'Unique identifier for the shipping method' },
            { label: 'shipping_zone_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_zone_id', documentation: 'Zone identifier for the shipping method' },
            { label: 'shipping_name', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_name', documentation: 'Name of the shipping method' },
            { label: 'rate_name', kind: monaco.languages.CompletionItemKind.Property, insertText: 'rate_name', documentation: 'Display name of the shipping rate' },
            { label: 'price', kind: monaco.languages.CompletionItemKind.Property, insertText: 'price', documentation: 'Cost of the shipping method (as string)' },
            { label: 'value', kind: monaco.languages.CompletionItemKind.Property, insertText: 'value', documentation: 'Numeric value identifier for the shipping method' },
            { label: 'label', kind: monaco.languages.CompletionItemKind.Property, insertText: 'label', documentation: 'Display label with name and price' },
          ]
        };
      } else if (/shippingMethods\[\d+\]\./.test(textUntilPosition) && !textUntilPosition.includes('cart.')) {
        // Detecta patrones como shippingMethods[222]. con números específicos
        return {
          suggestions: [
            { label: 'shipping_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_id', documentation: 'Unique identifier for the shipping method' },
            { label: 'shipping_zone_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_zone_id', documentation: 'Zone identifier for the shipping method' },
            { label: 'shipping_name', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_name', documentation: 'Name of the shipping method' },
            { label: 'rate_name', kind: monaco.languages.CompletionItemKind.Property, insertText: 'rate_name', documentation: 'Display name of the shipping rate' },
            { label: 'price', kind: monaco.languages.CompletionItemKind.Property, insertText: 'price', documentation: 'Cost of the shipping method (as string)' },
            { label: 'value', kind: monaco.languages.CompletionItemKind.Property, insertText: 'value', documentation: 'Numeric value identifier for the shipping method' },
            { label: 'label', kind: monaco.languages.CompletionItemKind.Property, insertText: 'label', documentation: 'Display label with name and price' },
          ]
        };
      } else if (textUntilPosition.includes('shippingMethods[') && !textUntilPosition.includes('cart.') && !textUntilPosition.includes('].')) {
        return {
          suggestions: [
            { label: '0', kind: monaco.languages.CompletionItemKind.Constant, insertText: '0', documentation: 'Access first shipping method' },
            { label: '1', kind: monaco.languages.CompletionItemKind.Constant, insertText: '1', documentation: 'Access second shipping method' },
            { label: 'INDEX', kind: monaco.languages.CompletionItemKind.Variable, insertText: 'INDEX', documentation: 'Replace with the desired index number' },
          ]
        };
      } else if (textUntilPosition.endsWith('cart.')) {
        return {
          suggestions: [
            { label: 'products', kind: monaco.languages.CompletionItemKind.Property, insertText: 'products' },
            { label: 'order_code', kind: monaco.languages.CompletionItemKind.Property, insertText: 'order_code' },
            { label: 'subtotal_price', kind: monaco.languages.CompletionItemKind.Property, insertText: 'subtotal_price' },
            { label: 'total_price', kind: monaco.languages.CompletionItemKind.Property, insertText: 'total_price' },
            { label: 'client', kind: monaco.languages.CompletionItemKind.Property, insertText: 'client' },
            { label: 'shipping_price', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_price' },
            { label: 'tax_rate', kind: monaco.languages.CompletionItemKind.Property, insertText: 'tax_rate' },
            { label: 'total_tax', kind: monaco.languages.CompletionItemKind.Property, insertText: 'total_tax' },
            { label: 'total_price_with_tax', kind: monaco.languages.CompletionItemKind.Property, insertText: 'total_price_with_tax' },
            { label: 'total_discount', kind: monaco.languages.CompletionItemKind.Property, insertText: 'total_discount' },
            { label: 'coupon_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon_id' },
            { label: 'coupon_code', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon_code' },
            { label: 'coupon', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon' },
          ]
        };
      } else if (textUntilPosition.endsWith('cart.client.')) {
        return {
          suggestions: [
            { label: 'fname', kind: monaco.languages.CompletionItemKind.Property, insertText: 'fname' },
            { label: 'lname', kind: monaco.languages.CompletionItemKind.Property, insertText: 'lname' },
            { label: 'address', kind: monaco.languages.CompletionItemKind.Property, insertText: 'address' },
            { label: 'address2', kind: monaco.languages.CompletionItemKind.Property, insertText: 'address2' },
            { label: 'postal', kind: monaco.languages.CompletionItemKind.Property, insertText: 'postal' },
            { label: 'country', kind: monaco.languages.CompletionItemKind.Property, insertText: 'country' },
            { label: 'state', kind: monaco.languages.CompletionItemKind.Property, insertText: 'state' },
            { label: 'city', kind: monaco.languages.CompletionItemKind.Property, insertText: 'city' },
            { label: 'city_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'city_id' },
            { label: 's_fname', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_fname' },
            { label: 's_lname', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_lname' },
            { label: 's_address', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_address' },
            { label: 's_address2', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_address2' },
            { label: 's_postal', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_postal' },
            { label: 's_country', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_country' },
            { label: 's_state', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_state' },
            { label: 's_city', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_city' },
            { label: 's_city_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 's_city_id' },
            { label: 'email', kind: monaco.languages.CompletionItemKind.Property, insertText: 'email' },
            { label: 'phone', kind: monaco.languages.CompletionItemKind.Property, insertText: 'phone' },
            { label: 'isValidEmail', kind: monaco.languages.CompletionItemKind.Property, insertText: 'isValidEmail' },
            { label: 'isValidPhone', kind: monaco.languages.CompletionItemKind.Property, insertText: 'isValidPhone' },
            { label: 'sameShippingBilling', kind: monaco.languages.CompletionItemKind.Property, insertText: 'sameShippingBilling' },
            { label: 'checkTerms', kind: monaco.languages.CompletionItemKind.Property, insertText: 'checkTerms' },
            { label: 'shipping_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_id' },
            { label: 'terms', kind: monaco.languages.CompletionItemKind.Property, insertText: 'terms' },
            { label: 'shipping_radio', kind: monaco.languages.CompletionItemKind.Property, insertText: 'shipping_radio' },
          ]
        };
      } else if (textUntilPosition.endsWith('cart.coupon.')) {
        return {
          suggestions: [
            { label: 'coupon_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon_id' },
            { label: 'coupon_name', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon_name' },
            { label: 'coupon_code', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon_code' },
            { label: 'coupon_type_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon_type_id' },
            { label: 'coupon_type', kind: monaco.languages.CompletionItemKind.Property, insertText: 'coupon_type' },
            { label: 'redemptions', kind: monaco.languages.CompletionItemKind.Property, insertText: 'redemptions' },
            { label: 'conditions', kind: monaco.languages.CompletionItemKind.Property, insertText: 'conditions' },
          ]
        };
      } else if (textUntilPosition.endsWith('cart.coupon.conditions.')) {
        return {
          suggestions: [
            { label: 'type', kind: monaco.languages.CompletionItemKind.Property, insertText: 'type' },
            { label: 'amount', kind: monaco.languages.CompletionItemKind.Property, insertText: 'amount' },
            { label: 'products', kind: monaco.languages.CompletionItemKind.Property, insertText: 'products' },
            { label: 'redemptionLimits', kind: monaco.languages.CompletionItemKind.Property, insertText: 'redemptionLimits' },
            { label: 'maximumDiscountUses', kind: monaco.languages.CompletionItemKind.Property, insertText: 'maximumDiscountUses' },
            { label: 'applyToSpecificProducts', kind: monaco.languages.CompletionItemKind.Property, insertText: 'applyToSpecificProducts' },
            { label: 'minimumPurchaseRequirements', kind: monaco.languages.CompletionItemKind.Property, insertText: 'minimumPurchaseRequirements' },
          ]
        };
      } else if (textUntilPosition.endsWith('cart.coupon.conditions.redemptionLimits.')) {
        return {
          suggestions: [
            { label: 'endDate', kind: monaco.languages.CompletionItemKind.Property, insertText: 'endDate' },
            { label: 'startDate', kind: monaco.languages.CompletionItemKind.Property, insertText: 'startDate' },
            { label: 'limitByDate', kind: monaco.languages.CompletionItemKind.Property, insertText: 'limitByDate' },
            { label: 'enableEndDate', kind: monaco.languages.CompletionItemKind.Property, insertText: 'enableEndDate' },
            { label: 'makeRecurring', kind: monaco.languages.CompletionItemKind.Property, insertText: 'makeRecurring' },
            { label: 'recurringType', kind: monaco.languages.CompletionItemKind.Property, insertText: 'recurringType' },
          ]
        };
      } else if (textUntilPosition.endsWith('cart.coupon.conditions.maximumDiscountUses.')) {
        return {
          suggestions: [
            { label: 'limit', kind: monaco.languages.CompletionItemKind.Property, insertText: 'limit' },
            { label: 'isLimited', kind: monaco.languages.CompletionItemKind.Property, insertText: 'isLimited' },
          ]
        };
      } else if (textUntilPosition.endsWith('cart.coupon.conditions.minimumPurchaseRequirements.')) {
        return {
          suggestions: [
            { label: 'type', kind: monaco.languages.CompletionItemKind.Property, insertText: 'type' },
            { label: 'value', kind: monaco.languages.CompletionItemKind.Property, insertText: 'value' },
          ]
        };
      } else if (textUntilPosition.includes('cart.products[')) {
        return {
          suggestions: [
            { label: 'order_product_id', kind: monaco.languages.CompletionItemKind.Property, insertText: 'order_product_id' },
            { label: 'product_name', kind: monaco.languages.CompletionItemKind.Property, insertText: 'product_name' },
            { label: 'product_price', kind: monaco.languages.CompletionItemKind.Property, insertText: 'product_price' },
            { label: 'product_quantity', kind: monaco.languages.CompletionItemKind.Property, insertText: 'product_quantity' },
            { label: 'product_sku', kind: monaco.languages.CompletionItemKind.Property, insertText: 'product_sku' },
            { label: 'product_image', kind: monaco.languages.CompletionItemKind.Property, insertText: 'product_image' },
            { label: 'tax_type', kind: monaco.languages.CompletionItemKind.Property, insertText: 'tax_type' },
            { label: 'weight', kind: monaco.languages.CompletionItemKind.Property, insertText: 'weight' },
            { label: 'length', kind: monaco.languages.CompletionItemKind.Property, insertText: 'length' },
            { label: 'width', kind: monaco.languages.CompletionItemKind.Property, insertText: 'width' },
            { label: 'height', kind: monaco.languages.CompletionItemKind.Property, insertText: 'height' },
            { label: 'category', kind: monaco.languages.CompletionItemKind.Property, insertText: 'category' },
            { label: 'bulk_tiered_discounts', kind: monaco.languages.CompletionItemKind.Property, insertText: 'bulk_tiered_discounts' },
            { label: 'v1_select', kind: monaco.languages.CompletionItemKind.Property, insertText: 'v1_select' },
            { label: 'v2_select', kind: monaco.languages.CompletionItemKind.Property, insertText: 'v2_select' },
            { label: 'custom_inputs', kind: monaco.languages.CompletionItemKind.Property, insertText: 'custom_inputs' },
            { label: 'timestamp', kind: monaco.languages.CompletionItemKind.Property, insertText: 'timestamp' },
            { label: 'product_tax', kind: monaco.languages.CompletionItemKind.Property, insertText: 'product_tax' },
            { label: 'tax_amount', kind: monaco.languages.CompletionItemKind.Property, insertText: 'tax_amount' },
            { label: 'product_discount', kind: monaco.languages.CompletionItemKind.Property, insertText: 'product_discount' },
          ]
        };
      } else if (textUntilPosition.includes('cart.products[') && textUntilPosition.endsWith('custom_inputs[')) {
        return {
          suggestions: [
            { label: 'name', kind: monaco.languages.CompletionItemKind.Property, insertText: 'name' },
            { label: 'value', kind: monaco.languages.CompletionItemKind.Property, insertText: 'value' },
          ]
        };
      }
      
      return { suggestions: [] };
    }
  });
  
  // Return both providers for cleanup
  return [provider1, provider2];
};
